import { useEffect, useState } from 'react';
import { Link, useLocation, useRoute } from 'wouter';
import Header from '@/components/layout/header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, AlertCircle, Save, BarChart3, Camera } from 'lucide-react';
import { BrandingForm, normalizeBranding } from '@/components/activations/BrandingForm';
import { EventStatusBadge } from '@/components/activations/statusBadges';
import { ActivationsTab } from '@/components/activations/ActivationsTab';
import { AssetsTab } from '@/components/activations/AssetsTab';
import { useActivationEvent, useUpdateActivationEvent } from '@/hooks/useActivationEvents';
import type { ActivationEventStatus } from '@/types/activations';
import type { ActivationBrandingRequest } from '@/types/generated/activations.generated';

const EVENT_STATUSES: ActivationEventStatus[] = ['Draft', 'Active', 'Ended', 'Cancelled'];

export default function ActivationEventDetailPage() {
  const [, navigate] = useLocation();
  const [, params] = useRoute('/activations/events/:eventId');
  const eventId = params?.eventId;

  const { data: event, isLoading, isError, error } = useActivationEvent(eventId);
  const updateMutation = useUpdateActivationEvent();

  const [name, setName] = useState('');
  const [nameAr, setNameAr] = useState('');
  const [status, setStatus] = useState<ActivationEventStatus>('Draft');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [branding, setBranding] = useState<ActivationBrandingRequest>({});

  useEffect(() => {
    if (event) {
      setName(event.name ?? '');
      setNameAr(event.nameAr ?? '');
      setStatus(event.status);
      setStartDate(event.startDate ? event.startDate.slice(0, 10) : '');
      setEndDate(event.endDate ? event.endDate.slice(0, 10) : '');
      setBranding(event.branding ?? {});
    }
  }, [event]);

  const handleSave = async () => {
    if (!eventId) return;
    await updateMutation.mutateAsync({
      id: eventId,
      data: {
        name: name.trim(),
        nameAr: nameAr.trim() || null,
        status,
        startDate: startDate ? new Date(startDate).toISOString() : null,
        endDate: endDate ? new Date(endDate).toISOString() : null,
        branding: normalizeBranding(branding),
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex-1 p-6" data-testid="event-detail-loading">
        <Skeleton className="mb-4 h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (isError || !event) {
    return (
      <div className="flex-1 p-6">
        <Alert variant="destructive" data-testid="event-detail-error">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Failed to load event</AlertTitle>
          <AlertDescription>{(error as Error)?.message || 'Event not found.'}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="flex-1" data-testid="event-detail-page">
      <Header title={event.name ?? ''} description={`Slug: ${event.slug}`}>
        <EventStatusBadge status={event.status} />
        <Button variant="outline" asChild>
          <Link href={`/activations/events/${event.id}/operator`} data-testid="operator-console-link">
            <Camera className="mr-2 h-4 w-4" />
            Operator console
          </Link>
        </Button>
        <Button variant="outline" asChild>
          <Link href={`/activations/events/${event.id}/results`} data-testid="view-results-link">
            <BarChart3 className="mr-2 h-4 w-4" />
            Results &amp; leads
          </Link>
        </Button>
        <Button variant="outline" onClick={() => navigate('/activations/events')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
      </Header>

      <div className="p-6">
        <Tabs defaultValue="activations">
          <TabsList>
            <TabsTrigger value="activations" data-testid="tab-activations">Activations</TabsTrigger>
            <TabsTrigger value="assets" data-testid="tab-assets">Assets</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="activations" className="pt-4">
            <ActivationsTab eventId={event.id} />
          </TabsContent>

          <TabsContent value="assets" className="pt-4">
            <AssetsTab eventId={event.id} />
          </TabsContent>

          <TabsContent value="settings" className="pt-4">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Event details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="ed-name">Name</Label>
                      <Input id="ed-name" data-testid="event-detail-name" value={name} onChange={(e) => setName(e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="ed-name-ar">Name (Arabic)</Label>
                      <Input id="ed-name-ar" data-testid="event-detail-name-ar" dir="rtl" value={nameAr} onChange={(e) => setNameAr(e.target.value)} />
                    </div>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="ed-status">Status</Label>
                      <Select value={status} onValueChange={(v) => setStatus(v as ActivationEventStatus)}>
                        <SelectTrigger id="ed-status" data-testid="event-detail-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {EVENT_STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="ed-start">Start date</Label>
                      <Input id="ed-start" data-testid="event-detail-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="ed-end">End date</Label>
                      <Input id="ed-end" data-testid="event-detail-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Event branding</CardTitle>
                </CardHeader>
                <CardContent>
                  <BrandingForm value={branding} onChange={setBranding} idPrefix="event-brand" />
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button onClick={handleSave} disabled={updateMutation.isPending} data-testid="save-event-button">
                  <Save className="mr-2 h-4 w-4" />
                  {updateMutation.isPending ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
